import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, momentLocalizer, View } from 'react-big-calendar'
import moment from 'moment-timezone'
import 'moment/locale/es'
import 'moment/locale/pt'
import 'moment/locale/en-gb'
import '../../assets/css/views/absences.css'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'

import 'react-big-calendar/lib/css/react-big-calendar.css'
import {
  Button,
  ButtonGroup,
  DatePicker,
  IconInfo,
  List,
  ListItem,
  Modal,
  Select,
  SelectOption,
  TextArea,
} from '@veneer/core'
import absenceService from '../../services/generalApi/absence.service'
import {
  IAbsenceTypeDto,
  IAbsenceDto,
  IAbsenceCategoryDto,
} from '../../models/dto/generalApi/IAbsenceDto'
import AgendaManagement from '../../services/agendaApi/agendaManagement'
import { IGetAdviserListDto } from '../../models/dto/agendaApi/IGetAdviserListDto'
import { decryptText } from '../../utils/encryptionUtils'
import { IGetStateListDto } from '../../models/dto/userManagementApi/IGetStateListDto'
import { IGetCityListDto } from '../../models/dto/userManagementApi/IGetCityListDto'
import { IAbsenceIn, IAbsenceUpdateIn } from '../../models/input/generalApi/IAbsenceIn'

import LoaderModal from '../../components/Loader/loaderModal'
import Loader from '../../components/Loader/loader'
import dataListService from '../../services/generalApi/dataList.service'
import { ICountryDto } from '../../models/dto/generalApi/ICountryDto'
import { RolesEnum } from '../../models/enum/RolesEnum'
import { IUserByRolDto } from '../../models/dto/generalApi/IUserDto'

interface Event {
  id: number
  title: string
  start: Date
  end: Date
  allDay?: boolean
  resource?: any
  name: string
  reason: string
  color: string
  comment?: string
  idTypeAbsence: number
}

export default function Absences() {
  const [t] = useTranslation('global')
  type SelectValue = number | string | boolean
  const languageId: any = useSelector((state: RootState) => state.languageId)
  const userState: any = useSelector((state: RootState) => state.user)

  const [events, setEvents] = useState<Event[]>([])
  const [view, setView] = useState<View>('month')

  const [showModalEdit, setShowModalEdit] = useState<boolean>(false)
  const [showModalCreate, setShowModalCreate] = useState<boolean>(false)
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [showModalError, setShowModalError] = useState<boolean>(false)
  const [showModalSuccess, setShowModalSuccess] = useState<boolean>(false)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [updateErrorMessage, setUpdateErrorMessage] = useState<string>('')
  const [errorCreateReason, setErrorCreateReason] = useState<any[]>([])
  const [showModalInfo, setShowModalInfo] = useState<boolean>(false)
  const [eventSelected, setEventSelected] = useState<Event>()
  const [comments, setComments] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [originalStartDate, setOriginalStartDate] = useState<string>('')
  const [originalEndDate, setOriginalEndDate] = useState<string>('')

  const [errorStart, setErrorStart] = useState<boolean>(false)
  const [errorEnd, setErrorEnd] = useState<boolean>(false)

  const [absenceTypes, setAbsenceTypes] = useState<SelectOption[]>([])
  const [absenceTypeSelected, setAbsenceTypeSelected] = useState<SelectValue[]>([])
  const [absenceCategories, setAbsenceCategories] = useState<SelectOption[]>([])
  const [absenceCategorySelected, setAbsenceCategorySelected] = useState<SelectValue[]>([])

  const [advisors, setAdvisors] = useState<SelectOption[]>([])
  const [advisorsFiltered, setAdvisorsFiltered] = useState<SelectOption[]>([])
  const [advisorsSelected, setAdvisorsSelected] = useState<SelectValue[]>([])
  const [staff, setStaff] = useState<SelectOption[]>([])
  const [staffSelected, setStaffSelected] = useState<SelectValue[]>([])

  const [states, setStates] = useState<SelectOption[]>([])
  const [stateSelected, setStateSelected] = useState<SelectValue[]>([])
  const [cities, setCities] = useState<SelectOption[]>([])
  const [citySelected, setCitySelected] = useState<SelectValue[]>([])
  const [countries, setCountries] = useState<SelectOption[]>([])
  const [countrySelected, setCountrySelected] = useState<SelectValue[]>([])

  const [disableCreateAbsence, setDisableCreateAbsence] = useState<boolean>(true)
  const [disableStates, setDisableStates] = useState<boolean>(true)
  const [disableCities, setDisableCities] = useState<boolean>(true)
  const [disableCategory, setDisableCategory] = useState<boolean>(true)
  const [disableAdvisors, setDisableAdvisors] = useState<boolean>(true)
  const [editing, setEditing] = useState<boolean>(false)
  const [disableStaff, setDisableStaff] = useState<boolean>(true)

  const [loadingModal, setLoadingModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  const [disableOnCreateAbsence, setDisableOnCreateAbsence] = useState<boolean>(true)

  enum AbsenceCategories {
    advisor = 1,
    city = 2,
    state = 3,
    country = 4,
    supervisor = 5,
    trainer = 6,
    coordinator = 7,
  }

  const messages = {
    date: t('header.date'),
    time: t('header.time'),
    event: t('header.event'),
    allDay: t('header.allDay'),
    week: t('header.week'),
    work_week: t('header.work_week'),
    day: t('header.day'),
    month: t('header.month'),
    previous: t('header.previous'),
    next: t('header.next'),
    yesterday: t('header.yesterday'),
    tomorrow: t('header.tomorrow'),
    today: t('header.today'),
    agenda: t('header.agenda'),
    noEventsInRange: t('header.noEventsInRange'),
    showMore: (count: number) => `${t('header.showMore')} (${count})`,
  }

  useEffect(() => {
    getAbsences()
    getAbsencesTypesData()
    getAdvisorsData()
    getCountriesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getAbsences()
    getAbsencesTypesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageId.value])

  useEffect(() => {
    if (advisors.length > 0 && absenceTypes.length > 0 && countries.length > 0) {
      setDisableCreateAbsence(false)
    } else {
      setDisableCreateAbsence(true)
    }
  }, [advisors, absenceTypes, countries])

  useEffect(() => {
    if (states.length > 0) {
      setDisableStates(false)
    } else {
      setDisableStates(true)
    }
  }, [states])

  useEffect(() => {
    if (cities.length > 0) {
      setDisableCities(false)
    } else {
      setDisableCities(true)
    }
  }, [cities])

  useEffect(() => {
    if (staff.length > 0) {
      setDisableStaff(false)
    } else {
      setDisableStaff(true)
    }
  }, [staff])

  useEffect(() => {
    if (absenceCategories.length > 0) {
      setDisableCategory(false)
    } else {
      setDisableCategory(true)
    }
  }, [absenceCategories])

  useEffect(() => {
    if (advisors.length > 0) {
      setDisableAdvisors(false)
    } else {
      setDisableAdvisors(true)
    }
  }, [advisors])

  const computedEditState = useMemo(() => {
    if (editing && startDate && endDate) {
      const refStart = originalStartDate || startDate
      const refEnd = originalEndDate || endDate
      const invalidStartDate = moment(refStart).isBefore(moment(), 'day')
      const invalidEndDate = moment(refEnd).isBefore(moment(), 'day')

      return {
        disableEdit: (invalidStartDate && invalidEndDate) || errorStart || errorEnd,
        disableDelete: invalidStartDate,
        disableStartDateEdit: invalidStartDate,
        disableEndDateEdit: invalidEndDate,
      }
    }
    return {
      disableEdit: true,
      disableDelete: true,
      disableStartDateEdit: true,
      disableEndDateEdit: true,
    }
  }, [editing, startDate, endDate, originalStartDate, originalEndDate, errorStart, errorEnd])

  useEffect(() => {
    if (!editing) {
      if (absenceTypeSelected.length > 0) {
        getAbsenceCategories()
      } else {
        clearAbsenceTypeData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absenceTypeSelected, editing])

  const clearAbsenceTypeData = () => {
    if (countrySelected.length > 0) {
      setCountrySelected([])
    }
    if (absenceCategorySelected.length > 0) {
      setAbsenceCategorySelected([])
    }
    if (absenceCategories.length > 0) {
      setAbsenceCategories([])
    }
  }

  useEffect(() => {
    if (countrySelected.length > 0) {
      getStatesData()
    } else {
      resetLocationData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countrySelected])

  useEffect(() => {
    if (stateSelected.length > 0) {
      getCitiesData()
    } else {
      resetCityData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateSelected])

  const resetLocationData = () => {
    setStates([])
    setStateSelected([])
    setStaff([])
    setStaffSelected([])
  }

  const resetCityData = () => {
    setCities([])
    setCitySelected([])
  }

  useEffect(() => {
    if (absenceCategorySelected.length > 0 && countrySelected.length > 0) {
      const roleMapping: any = {
        [AbsenceCategories.supervisor]: RolesEnum.Supervisor,
        [AbsenceCategories.trainer]: RolesEnum.Entrenador,
        [AbsenceCategories.coordinator]: RolesEnum.Coordinador,
      }
      const categorySelected = roleMapping[Number(absenceCategorySelected)]

      if (categorySelected !== undefined) {
        getStaffData(categorySelected)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absenceCategorySelected, countrySelected])

  const validateForm = ({
    absenceTypeSelected,
    absenceCategorySelected,
    startDate,
    endDate,
    advisorsSelected,
    citySelected,
    stateSelected,
    countrySelected,
    staffSelected,
  }: {
    absenceTypeSelected: SelectValue[]
    absenceCategorySelected: SelectValue[]
    startDate: string
    endDate: string
    advisorsSelected: SelectValue[]
    citySelected: SelectValue[]
    stateSelected: SelectValue[]
    countrySelected: SelectValue[]
    staffSelected: SelectValue[]
  }): boolean => {
    if (
      !absenceTypeSelected ||
      !absenceCategorySelected ||
      startDate.length === 0 ||
      endDate.length === 0
    ) {
      return false
    }

    switch (Number(absenceCategorySelected)) {
      case AbsenceCategories.advisor:
        return advisorsSelected.length > 0
      case AbsenceCategories.city:
        return citySelected.length > 0
      case AbsenceCategories.state:
        return stateSelected.length > 0
      case AbsenceCategories.country:
        return countrySelected.length > 0
      case AbsenceCategories.supervisor:
      case AbsenceCategories.trainer:
      case AbsenceCategories.coordinator:
        return staffSelected.length > 0
      default:
        return false
    }
  }

  const isFormValid = useMemo(() => {
    if (absenceTypeSelected && absenceCategorySelected && startDate && endDate) {
      return validateForm({
        absenceTypeSelected,
        absenceCategorySelected,
        startDate,
        endDate,
        advisorsSelected,
        citySelected,
        stateSelected,
        countrySelected,
        staffSelected,
      })
    }
    return false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    absenceTypeSelected,
    absenceCategorySelected,
    startDate,
    endDate,
    advisorsSelected,
    citySelected,
    stateSelected,
    countrySelected,
    staffSelected,
  ])

  useEffect(() => {
    setDisableOnCreateAbsence(!isFormValid || errorStart || errorEnd)
  }, [isFormValid, errorStart, errorEnd])

  const getAbsencesTypesData = () => {
    try {
      absenceService.getAbsencesType(languageId.value).then((r) => {
        fillAbsencesTypes(r)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getStaffData = (roleStaff: number) => {
    try {
      dataListService.getUsersByRoleAndCountry(Number(countrySelected), roleStaff).then((r) => {
        fillStaff(r)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getAdvisorsData = () => {
    try {
      AgendaManagement.getAdvisorsBySupervisors(0).then((r: IGetAdviserListDto[]) => {
        const uniqueAdvisers = r.filter(
          (adviser, index, self) => self.findIndex((a) => a.userID === adviser.userID) === index,
        )
        fillAdvisorData(uniqueAdvisers)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getAbsences = () => {
    setLoading(true)
    try {
      absenceService.getAbsences(languageId.value).then((r) => {
        fillAbsences(r)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getStatesData = () => {
    try {
      AgendaManagement.getStateByCountry(Number(countrySelected)).then((r) => {
        fillStates(r)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getCountriesData = () => {
    try {
      dataListService.getCountryListBySubregion().then((r) => {
        fillCountries(r)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getCitiesData = () => {
    try {
      AgendaManagement.getCityByState(Number(stateSelected)).then((r) => {
        fillCities(r)
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getAbsenceCategories = () => {
    try {
      absenceService
        .getAbsencesCategory(languageId.value, Number(absenceTypeSelected))
        .then((r) => {
          const validatedCategories = validateAbsencesCategories(r)
          fillCategories(validatedCategories!)
        })
        .catch((error) => {
          console.error(error)
        })
    } catch (error) {
      console.error(error)
    }
  }

  const fillAbsencesTypes = (data: IAbsenceTypeDto[]) => {
    const absencesTypesTemp: SelectOption[] = []

    for (let i = 0; i < data.length; i++) {
      const absenceType: IAbsenceTypeDto = data[i]
      let absenceTypetemp: SelectOption
      absenceTypetemp = {
        value: absenceType.idType,
        label: absenceType.label,
      }
      absencesTypesTemp.push(absenceTypetemp)
    }
    setAbsenceTypes(absencesTypesTemp)
  }

  const fillAdvisorData = (data: IGetAdviserListDto[]) => {
    const advisorsTemp: SelectOption[] = []

    for (let i = 0; i < data.length; i++) {
      const advisor: IGetAdviserListDto = data[i]
      let advisorTemp: SelectOption
      advisorTemp = {
        value: advisor.userID,
        label: advisor.userName + ' ' + advisor.lastName,
      }
      advisorsTemp.push(advisorTemp)
    }
    setAdvisors(advisorsTemp)
    setAdvisorsFiltered(advisorsTemp)
  }

  function getColorByAbsenceType(absenceTypeId: number): string {
    switch (absenceTypeId) {
      case 1:
        return '#A8E6CF' // Vacations: Verde claro
      case 2:
        return '#ADD8E6' // Holiday not Working: Azul claro
      case 3:
        return '#D3D3D3' // Compensatory or Break: Gris claro
      case 4:
        return '#FF6B6B' // Inability: Rojo
      case 5:
        return '#FFD700' // Permission: Amarillo
      case 6:
        return '#4682B4' // Homeoffice: Azul oscuro
      case 7:
        return '#FF8C00' // Sanction: Naranja oscuro
      case 8:
        return '#6A0DAD' // Contingency/Robbery: Morado oscuro
      case 9:
        return '#1E90FF' // Training: Púrpura/lila
      case 10:
        return '#E6BE8A' // Omnichat: Oro
      default:
        return '#027aae' // Valor por defecto si no coincide
    }
  }

  const fillAbsences = (data: IAbsenceDto[]) => {
    const eventsTemp: Event[] = []
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    data.forEach((item) => {
      const absences = JSON.parse(item.data) as {
        AbsenceID: number
        StartDate: string
        EndDate: string
        IdAbsence: number
        TypeAbsence: string
        Comment: string
        Name: string
        Removable: boolean
        CurrentDate: string
      }[]

      absences.forEach((absence) => {
        const eventTemp: Event = {
          id: absence.AbsenceID,
          title: absence.TypeAbsence + ' ' + absence.Name,
          start: moment.tz(absence.StartDate, 'YYYY-MM-DD', userTimeZone).startOf('day').toDate(),
          end: moment.tz(absence.EndDate, 'YYYY-MM-DD', userTimeZone).endOf('day').toDate(),
          allDay: true,
          resource: absence,
          name: absence.Name,
          reason: absence.TypeAbsence,
          color: getColorByAbsenceType(absence.IdAbsence),
          comment: absence.Comment,
          idTypeAbsence: absence.IdAbsence,
        }
        eventsTemp.push(eventTemp)
      })
    })
    setEvents(eventsTemp)
    setLoading(false)
  }

  const fillStates = (data: IGetStateListDto[]) => {
    const statesTemp: SelectOption[] = []

    for (let i = 0; i < data.length; i++) {
      const state: IGetStateListDto = data[i]
      let stateTemp: SelectOption
      stateTemp = {
        value: state.stateID,
        label: state.stateName,
      }
      statesTemp.push(stateTemp)
    }
    setStates(statesTemp)
  }

  const fillCountries = (data: ICountryDto[]) => {
    const countriesTemp: SelectOption[] = []

    for (let i = 0; i < data.length; i++) {
      const country: ICountryDto = data[i]
      let countryTemp: SelectOption
      countryTemp = {
        value: country.contryId,
        label: country.countryName,
      }
      countriesTemp.push(countryTemp)
    }
    setCountries(countriesTemp)
  }

  const fillCities = (data: IGetCityListDto[]) => {
    const citiesTemp: SelectOption[] = []
    for (let i = 0; i < data.length; i++) {
      const city: IGetCityListDto = data[i]
      let cityTemp: SelectOption
      cityTemp = {
        value: city.cityID,
        label: city.cityName,
      }
      citiesTemp.push(cityTemp)
    }
    setCities(citiesTemp)
  }

  const validateAbsencesCategories = (data: IAbsenceCategoryDto[]) => {
    const userRole = decryptText(userState.user.roleId)
    switch (Number(userRole)) {
      case Number(RolesEnum.Supervisor):
        return data.filter(
          (item) =>
            Number(item.idCategory) === AbsenceCategories.advisor ||
            Number(item.idCategory) === AbsenceCategories.supervisor,
        )
      case Number(RolesEnum.Entrenador):
        return data.filter((item) => Number(item.idCategory) === AbsenceCategories.trainer)
      case Number(RolesEnum.Coordinador):
        return data.filter(
          (item) =>
            Number(item.idCategory) === AbsenceCategories.coordinator ||
            Number(item.idCategory) === AbsenceCategories.advisor ||
            Number(item.idCategory) === AbsenceCategories.supervisor,
        )
      case Number(RolesEnum.BackOffice):
        return data
      default:
        return []
    }
  }

  const fillCategories = (data: IAbsenceCategoryDto[]) => {
    const absencesCategoriesTemp: SelectOption[] = []

    for (let i = 0; i < data.length; i++) {
      const absenceCategory: IAbsenceCategoryDto = data[i]
      let absenceCategorytemp: SelectOption
      absenceCategorytemp = {
        value: absenceCategory.idCategory,
        label: absenceCategory.category,
      }
      absencesCategoriesTemp.push(absenceCategorytemp)
    }
    setAbsenceCategories(absencesCategoriesTemp)
  }

  const fillStaff = (data: IUserByRolDto[]) => {
    const staffTemp: SelectOption[] = []

    for (let i = 0; i < data.length; i++) {
      const user: IUserByRolDto = data[i]
      let userTemp: SelectOption
      userTemp = {
        value: user.userID,
        label: user.name,
      }
      staffTemp.push(userTemp)
    }
    setStaff(staffTemp)
  }

  const culture = useMemo(() => {
    if (languageId.value === 1) {
      return 'en-gb'
    } else if (languageId.value === 2) {
      return 'es'
    } else if (languageId.value === 3) {
      return 'pt'
    } else {
      return 'en-gb'
    }
  }, [languageId.value])

  const localizer = momentLocalizer(moment)

  const handleSelectEvent = (event: Event) => {
    const formattedStart = moment(event.start).startOf('day').format('YYYY-MM-DD')
    const formattedEnd = moment(event.end).endOf('day').format('YYYY-MM-DD')
    setEditing(true)
    setEventSelected(event)
    setComments(event.comment ? event.comment : '')
    setStartDate(formattedStart)
    setOriginalStartDate(formattedStart)
    setEndDate(formattedEnd)
    setOriginalEndDate(formattedEnd)
    setAbsenceTypeSelected([String(event.idTypeAbsence)])
    setShowModalEdit(true)
  }

  const handleChangeSearch = (value: string) => {
    const filterArray = advisors.filter(
      (element) =>
        element.value.toString().toLowerCase().includes(String(value).toLowerCase()) ||
        element.label?.toString().toLowerCase().includes(String(value).toLowerCase()),
    )
    setAdvisorsFiltered(filterArray)
  }

  const handleChangeAdvisors = (value: SelectValue) => {
    const advisorsCopy = [...advisorsSelected]
    const isOptionSelected = advisorsCopy.includes(value)
    if (isOptionSelected) {
      const index = advisorsCopy.indexOf(value)
      advisorsCopy.splice(index, 1)
    } else {
      advisorsCopy.push(value)
    }
    setAdvisorsSelected(advisorsCopy)
  }

  const eventStyleGetter = (event: Event, _start: Date, _end: Date, _isSelected: boolean) => {
    let backgroundColor = event.color
    return {
      style: {
        backgroundColor,
        color: 'white',
        borderRadius: '5px',
        opacity: 0.8,
        border: '0px',
        display: 'block',
      },
    }
  }

  const colors = [
    { id: 1, name: t('header.vacations'), color: '#A8E6CF' }, // Vacations: Verde claro
    { id: 2, name: t('header.holidayNotWorking'), color: '#ADD8E6' }, // Holiday not Working: Azul claro
    { id: 3, name: t('header.restDay'), color: '#D3D3D3' }, // Compensatory or Break: Gris claro
    { id: 4, name: t('header.inability'), color: '#FF6B6B' }, // Inability: Rojo
    { id: 5, name: t('header.permission'), color: '#FFD700' }, // Permission: Amarillo
    { id: 6, name: t('header.homeoffice'), color: '#4682B4' }, // Homeoffice: Azul oscuro
    { id: 7, name: t('header.sanction'), color: '#FF8C00' }, // Sanction: Naranja oscuro
    { id: 8, name: t('header.contingencyRobbery'), color: '#6A0DAD' }, // Contingency/Robbery: Morado oscuro
    { id: 9, name: t('header.training'), color: '#1E90FF' }, // Training: Púrpura/Lila
    { id: 10, name: t('header.omnichat'), color: '#E6BE8A' }, // Omnichat: Oro
  ]

  const onDelete = async () => {
    setLoadingModal(true)
    if (eventSelected) {
      try {
        const response = await absenceService.deleteAbsence(eventSelected.id)
        const resultValue =
          typeof response === 'number' ? response : (response?.Result ?? response?.result ?? 0)
        if (resultValue !== 1) {
          setShowModalError(true)
        } else {
          setSuccessMessage('header.absenceDeleted')
          setShowModalSuccess(true)
          getAbsences()
        }
      } catch (error) {
        console.error('Error posting absence:', error)
      } finally {
        resetDeleteFormStates()
        setLoadingModal(false)
      }
    } else {
      resetDeleteFormStates()
    }
  }

  const resetDeleteFormStates = () => {
    setEventSelected(undefined)
    setComments('')
    setAbsenceTypeSelected([])
    setStartDate('')
    setEndDate('')
    setOriginalStartDate('')
    setOriginalEndDate('')
    setShowModalDelete(false)
    setShowModalEdit(false)
    setEditing(false)
  }

  const onEdit = async () => {
    setLoadingModal(true)
    if (eventSelected) {
      let absencesUpdateIn: IAbsenceUpdateIn = {
        idAbsence: eventSelected?.id,
        typeAbsence: Number(absenceTypeSelected),
        comment: comments,
        dateStart: moment(startDate).format('YYYY-MM-DD'),
        dateEnd: moment(endDate).format('YYYY-MM-DD'),
      }
      try {
        const response = await absenceService.updateAbsence(absencesUpdateIn)
        const rawValue =
          typeof response === 'number'
            ? response
            : (response?.Result ?? response?.result ?? response)
        const resultValue = typeof rawValue === 'string' ? Number(rawValue) : (rawValue ?? 0)
        if (resultValue !== 1) {
          const errorMessages: Record<number, string> = {
            2: t('modal.absenceNotEditable'),
            3: t('modal.absenceUpdateOverlapAdvisor'),
            4: t('modal.absenceUpdateOverlapGlobal'),
          }
          setUpdateErrorMessage(errorMessages[resultValue] ?? t('header.errorAbsences'))
          setShowModalError(true)
        } else {
          setSuccessMessage('modal.absenceUpdated')
          setShowModalSuccess(true)
          getAbsences()
        }
      } catch (error) {
        console.error('Error posting absence:', error)
      } finally {
        resetEditFormStates()
        setLoadingModal(false)
      }
    } else {
      resetEditFormStates()
    }
  }

  const resetEditFormStates = () => {
    setEventSelected(undefined)
    setComments('')
    setAbsenceTypeSelected([])
    setStartDate('')
    setEndDate('')
    setOriginalStartDate('')
    setOriginalEndDate('')
    setShowModalEdit(false)
    setEditing(false)
  }

  const onCreate = async () => {
    setLoadingModal(true)
    let categorySelected = Number(absenceCategorySelected)
    let absencesIn: IAbsenceIn[] = []

    switch (categorySelected) {
      case 1:
        advisorsSelected.forEach((advisor) => {
          absencesIn.push({
            advisorID: 0,
            cityID: null,
            stateID: null,
            countryID: null,
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            typeID: Number(absenceTypeSelected),
            userID: Number(advisor),
            descriptionText: comments,
          })
        })
        break

      case 2:
        absencesIn.push({
          advisorID: 0,
          cityID: citySelected.length > 0 ? Number(citySelected) : null,
          stateID: null,
          countryID: null,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          typeID: Number(absenceTypeSelected),
          userID: null,
          descriptionText: comments,
        })
        break

      case 3:
        absencesIn.push({
          advisorID: 0,
          cityID: null,
          stateID: stateSelected.length > 0 ? Number(stateSelected) : null,
          countryID: null,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          typeID: Number(absenceTypeSelected),
          userID: null,
          descriptionText: comments,
        })
        break

      case 4:
        absencesIn.push({
          advisorID: 0,
          cityID: null,
          stateID: null,
          countryID: countrySelected.length > 0 ? Number(countrySelected) : null,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          typeID: Number(absenceTypeSelected),
          userID: null,
          descriptionText: comments,
        })
        break

      default:
        absencesIn.push({
          advisorID: 0,
          cityID: null,
          stateID: null,
          countryID: null,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          typeID: Number(absenceTypeSelected),
          userID: Number(staffSelected),
          descriptionText: comments,
        })
        break
    }
    try {
      const response = await absenceService.postAbsence(absencesIn)
      if (response.length > 0) {
        setShowModalError(true)
        setErrorCreateReason(response)
      } else {
        setSuccessMessage('modal.absenceCreated')
        setShowModalSuccess(true)
        getAbsences()
      }
    } catch (error) {
      console.error('Error posting absence:', error)
    } finally {
      resetFormStates()
      setLoadingModal(false)
    }
  }

  const resetFormStates = () => {
    setStartDate('')
    setEndDate('')
    setComments('')
    setAbsenceTypeSelected([])
    setShowModalCreate(false)
    setErrorEnd(false)
    setErrorStart(false)

    if (advisorsSelected.length > 0) {
      setAdvisorsSelected([])
    }
  }

  const handleStartDateChange = (date: moment.Moment | undefined) => {
    const formattedStart = date ? moment(date).startOf('day').format('YYYY-MM-DD') : ''
    if (date && endDate.length > 0 && date.isAfter(moment(endDate), 'day')) {
      setErrorStart(true)
      setStartDate(formattedStart)
    } else {
      setErrorStart(false)
      setErrorEnd(false)
      setStartDate(formattedStart)
    }
  }

  const handleEndDateChange = (date: moment.Moment | undefined) => {
    const formattedEnd = date ? moment(date).endOf('day').format('YYYY-MM-DD') : ''
    if (date && startDate.length > 0 && date.isBefore(moment(startDate), 'day')) {
      setErrorEnd(true)
      setEndDate(formattedEnd)
    } else {
      setErrorEnd(false)
      setErrorStart(false)
      setEndDate(formattedEnd)
    }
  }

  return (
    <div className="absences">
      {loading && <Loader />}
      <h4 className="absencesTitle">{t('header.absences')}</h4>
      <div className="absencesTop">
        <Button
          className="buttonInfoAbsence"
          onClick={() => setShowModalInfo(true)}
          leadingIcon={<IconInfo />}
          appearance="secondary"
        />
        <Button
          disabled={disableCreateAbsence}
          className="buttonCreateAbsence"
          onClick={() => setShowModalCreate(true)}
        >
          {t('header.createAbsence')}
        </Button>
      </div>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        views={['month', 'week', 'day']}
        defaultView={view}
        onView={(newView: View) => setView(newView)}
        style={{ height: 500 }}
        onSelectEvent={handleSelectEvent}
        messages={messages}
        eventPropGetter={eventStyleGetter}
        culture={culture}
      />

      {loadingModal && <LoaderModal />}
      <div style={{ position: 'relative' }}>
        <Modal
          className="modalCreatetAbsences"
          align="center"
          id="modalCreatetAbsences"
          show={showModalCreate}
          onClose={resetFormStates}
          closeButton
          footer={
            <ButtonGroup className="modalBrandsConfirmCreationButtonGroup">
              <Button disabled={disableOnCreateAbsence} onClick={() => onCreate()}>
                {t('header.create')}
              </Button>
              <Button onClick={resetFormStates} appearance="secondary">
                {t('header.cancel')}
              </Button>
            </ButtonGroup>
          }
          title={t('header.createAbsence')}
        >
          <div>
            <Select
              className="absenceSelect"
              label={t('header.type')}
              onChange={(e) => setAbsenceTypeSelected([e.value])}
              onClear={() => setAbsenceTypeSelected([])}
              options={absenceTypes}
              value={absenceTypeSelected}
              placement="bottom-start"
              required
            />

            <Select
              className="absenceSelect"
              label={t('header.category')}
              onChange={(e) => setAbsenceCategorySelected([e.value])}
              onClear={() => setAbsenceCategorySelected([])}
              options={absenceCategories}
              value={absenceCategorySelected}
              placement="bottom-start"
              required
              disabled={disableCategory}
            />

            {absenceCategorySelected.length > 0 &&
              Number(absenceCategorySelected) !== AbsenceCategories.advisor && (
                <Select
                  className="absenceSelect"
                  label={t('header.countries')}
                  onChange={(e) => setCountrySelected([e.value])}
                  onClear={() => setCountrySelected([])}
                  options={countries}
                  value={countrySelected}
                  placement="bottom-start"
                  required
                />
              )}

            {absenceCategorySelected.length > 0 &&
              (Number(absenceCategorySelected) === AbsenceCategories.state ||
                Number(absenceCategorySelected) === AbsenceCategories.city) && (
                <Select
                  className="absenceSelect"
                  label={t('header.states')}
                  onChange={(e) => setStateSelected([e.value])}
                  onClear={() => setStateSelected([])}
                  options={states}
                  value={stateSelected}
                  placement="bottom-start"
                  disabled={disableStates}
                  required
                />
              )}

            {absenceCategorySelected.length > 0 &&
              Number(absenceCategorySelected) === AbsenceCategories.city && (
                <Select
                  className="absenceSelect"
                  label={t('header.cities')}
                  onChange={(e) => setCitySelected([e.value])}
                  onClear={() => setCitySelected([])}
                  options={cities}
                  value={citySelected}
                  placement="bottom-start"
                  disabled={disableCities}
                  required
                />
              )}
            {absenceCategorySelected.length > 0 &&
              Number(absenceCategorySelected) === AbsenceCategories.advisor && (
                <Select
                  className="absenceSelect"
                  label={t('header.advisors')}
                  onChange={(e) => handleChangeAdvisors(e.value)}
                  onClear={() => setAdvisorsSelected([])}
                  options={advisorsFiltered}
                  value={advisorsSelected}
                  placement="bottom-start"
                  multiple
                  required
                  disabled={disableAdvisors}
                  onSearch={handleChangeSearch}
                  showSearch
                />
              )}

            {absenceCategorySelected.length > 0 &&
              (Number(absenceCategorySelected) === AbsenceCategories.supervisor ||
                Number(absenceCategorySelected) === AbsenceCategories.trainer ||
                Number(absenceCategorySelected) === AbsenceCategories.coordinator) && (
                <Select
                  className="absenceSelect"
                  label={
                    Number(absenceCategorySelected) === AbsenceCategories.supervisor
                      ? t('header.supervisor')
                      : Number(absenceCategorySelected) === AbsenceCategories.trainer
                        ? t('header.trainer')
                        : t('header.coordinator')
                  }
                  onChange={(e) => setStaffSelected([e.value])}
                  onClear={() => setStaffSelected([])}
                  options={staff}
                  value={staffSelected}
                  placement="bottom-start"
                  required
                  disabled={disableStaff}
                />
              )}

            <TextArea
              className="absenceTextarea"
              id="absenceTextarea"
              label={t('header.comments')}
              type="text"
              onChange={setComments}
              value={comments}
            />

            <div className="absenceDatepickers">
              <DatePicker
                className="absenceDatepicker"
                calendarOrientation={['right', 'down']}
                label={t('header.stardDate')}
                onChange={(date) => handleStartDateChange(date as moment.Moment)}
                orientation="horizontal"
                placeholder="mm/dd/yyyy"
                value={startDate}
                required
                error={errorStart}
                isOutsideRange={(date) => date.isBefore(moment().subtract(2, 'days'), 'day')}
              />

              <DatePicker
                className="absenceDatepicker"
                calendarOrientation={['right', 'down']}
                label={t('header.endDate')}
                onChange={(date) => handleEndDateChange(date as moment.Moment)}
                orientation="horizontal"
                placeholder="mm/dd/yyyy"
                value={endDate}
                required
                error={errorEnd}
                isOutsideRange={(date) =>
                  startDate && moment(startDate).isValid()
                    ? date.isBefore(moment(startDate), 'day')
                    : false
                }
              />
            </div>
          </div>
        </Modal>
      </div>

      {loadingModal && <LoaderModal />}
      <div style={{ position: 'relative' }}>
        <Modal
          className="modalEditAbsences"
          align="center"
          id="modalEditAbsences"
          show={showModalEdit}
          onClose={() => resetEditFormStates()}
          closeButton
          footer={
            <ButtonGroup className="modalBrandsConfirmCreationButtonGroup">
              <Button disabled={computedEditState.disableEdit} onClick={() => onEdit()}>
                {t('header.edit')}
              </Button>
              <Button onClick={() => resetEditFormStates()} appearance="secondary">
                {t('header.cancel')}
              </Button>
              <Button
                disabled={computedEditState.disableDelete}
                onClick={() => setShowModalDelete(true)}
                appearance="secondary"
                colorScheme="negative"
              >
                {t('header.delete')}
              </Button>
            </ButtonGroup>
          }
          title={t('header.absence')}
        >
          <div>
            {eventSelected && <b>{eventSelected.name}</b>}
            <br />

            <Select
              className="absenceSelect"
              label={t('header.type')}
              onChange={(e) => setAbsenceTypeSelected([e.value])}
              onClear={() => setAbsenceTypeSelected([])}
              options={absenceTypes}
              value={absenceTypeSelected}
              placement="bottom-start"
              required
              disabled={computedEditState.disableEdit}
            />
            <TextArea
              className="absenceTextarea"
              id="absenceTextarea"
              label={t('header.comments')}
              type="text"
              onChange={setComments}
              value={comments}
              disabled={computedEditState.disableEdit}
            />
            <div className="absenceDatepickers">
              <DatePicker
                className="absenceDatepicker"
                calendarOrientation={['right', 'down']}
                label={t('header.stardDate')}
                onChange={(date) => handleStartDateChange(date as moment.Moment)}
                orientation="horizontal"
                placeholder="mm/dd/yyyy"
                value={startDate}
                required
                error={errorStart}
                isOutsideRange={(date) => date.isBefore(moment().subtract(2, 'days'), 'day')}
                disabled={computedEditState.disableStartDateEdit}
                clearIcon={false}
              />

              <DatePicker
                className="absenceDatepicker"
                calendarOrientation={['right', 'down']}
                label={t('header.endDate')}
                onChange={(date) => handleEndDateChange(date as moment.Moment)}
                orientation="horizontal"
                placeholder="mm/dd/yyyy"
                value={endDate}
                required
                error={errorEnd}
                isOutsideRange={(date) => {
                  if (date.isBefore(moment().subtract(2, 'days'), 'day')) return true
                  if (
                    startDate &&
                    moment(startDate).isValid() &&
                    date.isBefore(moment(startDate), 'day')
                  )
                    return true
                  return false
                }}
                disabled={computedEditState.disableEndDateEdit}
                clearIcon={false}
              />
            </div>
          </div>
        </Modal>
      </div>

      <div style={{ position: 'relative' }}>
        <Modal
          align="center"
          id="modalDeleteAbsence"
          show={showModalDelete}
          onClose={() => setShowModalDelete(false)}
          footer={
            <ButtonGroup className="deletebsenceButtongrouop">
              <Button onClick={onDelete}>{t('header.yes')}</Button>
              <Button onClick={() => setShowModalDelete(false)} appearance="secondary">
                {t('header.cancel')}
              </Button>
            </ButtonGroup>
          }
          title={t('header.deleteAbsence')}
        >
          <p>{t('header.questionDeleteAbsence')}</p>
        </Modal>
      </div>

      <div style={{ position: 'relative' }}>
        <Modal
          align="center"
          id="modalAbsenceSuccess"
          show={showModalSuccess}
          onClose={() => {
            setShowModalSuccess(false)
            setSuccessMessage('')
          }}
          footer={
            <ButtonGroup className="deletebsenceButtongrouop">
              <Button
                onClick={() => {
                  setShowModalSuccess(false)
                  setSuccessMessage('')
                }}
              >
                {t('header.accept')}
              </Button>
            </ButtonGroup>
          }
          title={t('header.success')}
        >
          <p>{t(successMessage as any)}</p>
        </Modal>
      </div>

      <div style={{ position: 'relative' }}>
        <Modal
          align="center"
          id="modalAbsenceError"
          show={showModalError}
          onClose={() => {
            setShowModalError(false)
            setUpdateErrorMessage('')
          }}
          footer={
            <ButtonGroup className="deletebsenceButtongrouop">
              <Button
                onClick={() => {
                  setErrorCreateReason([])
                  setUpdateErrorMessage('')
                  setShowModalError(false)
                }}
              >
                {t('header.accept')}
              </Button>
            </ButtonGroup>
          }
          title={t('header.error')}
        >
          {updateErrorMessage.length > 0 ? (
            <p>{updateErrorMessage}</p>
          ) : errorCreateReason.length > 0 ? (
            <>
              <p>{t('header.cannotCreateAbsenceTo')}</p>
              {errorCreateReason.map((error, index) => (
                <div key={index}>
                  <p>{`${index + 1}. ${error.userName} ${error.userLastName}`}</p>
                  <p>
                    {error.reason === 1
                      ? t('header.AbsenceAlreadyExists')
                      : error.reason === 2
                        ? t('header.AbsenceCheckInCheckOut')
                        : t('header.AbsenceGlobalRestriction')}
                  </p>
                </div>
              ))}
            </>
          ) : (
            <p>{t('header.errorAbsences')}</p>
          )}
        </Modal>
      </div>

      <div style={{ position: 'relative' }}>
        <Modal
          align="center"
          id="modalAbsenceInfo"
          show={showModalInfo}
          onClose={() => setShowModalInfo(false)}
          footer={
            <ButtonGroup className="deletebsenceButtongrouop">
              <Button onClick={() => setShowModalInfo(false)}>{t('header.close')}</Button>
            </ButtonGroup>
          }
          title={t('header.information')}
        >
          <List>
            {colors.map((color) => (
              <ListItem
                key={color.id}
                leadingArea={color.name}
                trailingArea={
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: color.color,
                    }}
                  />
                }
              />
            ))}
          </List>
        </Modal>
      </div>
    </div>
  )
}
